import * as React from "react";
import { Link } from "@tanstack/react-router";

import type { PostBlock } from "@/content/posts";

const LINK_CLASS =
  "text-brand-700 underline underline-offset-4 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700";

const LINK_PATTERN = /\[([^\]]+)\]\(([^)\s]+)\)/g;

/**
 * Tokenizes `[anchor](href)` out of plain text. No HTML is ever injected —
 * every segment is rendered as a React child, so text stays escaped.
 */
export function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  LINK_PATTERN.lastIndex = 0;

  while ((match = LINK_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const label = match[1]!;
    const href = match[2]!;
    const key = `${keyPrefix}-link-${match.index}`;

    if (href.startsWith("/")) {
      nodes.push(
        <Link key={key} to={href as never} className={LINK_CLASS}>
          {label}
        </Link>,
      );
    } else {
      nodes.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
          {label}
        </a>,
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function PostBody({ blocks }: { blocks: PostBlock[] }) {
  return (
    <div className="space-y-5">
      {blocks.map((block, i) => {
        const key = `block-${i}`;
        switch (block.type) {
          case "h2":
            return (
              <h2
                key={key}
                className="mt-12 font-display text-h2 text-ink-900"
                style={{ textWrap: "balance" }}
              >
                {block.text}
              </h2>
            );
          case "h3":
            return (
              <h3 key={key} className="mt-8 font-display text-h3 text-ink-900">
                {block.text}
              </h3>
            );
          case "ul":
            return (
              <ul
                key={key}
                className="ml-5 max-w-[68ch] list-disc space-y-3 text-body-lg text-ink-600"
              >
                {block.items.map((item, j) => (
                  <li key={`${key}-${j}`} className="pl-1">
                    {renderInline(item, `${key}-${j}`)}
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol
                key={key}
                className="ml-5 max-w-[68ch] list-decimal space-y-3 text-body-lg text-ink-600"
              >
                {block.items.map((item, j) => (
                  <li key={`${key}-${j}`} className="pl-1">
                    {renderInline(item, `${key}-${j}`)}
                  </li>
                ))}
              </ol>
            );
          case "callout":
            return (
              <div
                key={key}
                className="mt-10 rounded-2xl border border-brand-200 bg-brand-50 p-6 text-body-lg text-ink-800 md:p-8"
              >
                {renderInline(block.text, key)}
              </div>
            );
          case "p":
          default:
            return (
              <p key={key} className="max-w-[68ch] text-body-lg text-ink-600">
                {renderInline(block.text, key)}
              </p>
            );
        }
      })}
    </div>
  );
}

export default PostBody;
